/**
 * Mission terminal — two-tab placeholder for the post-tutorial
 * progression hub. The terminal currently has nothing real to offer,
 * but it's the in-world handle the player will use later for the main
 * campaign and randomised contracts. So instead of fake content, we
 * present a deliberately polished "coming soon" surface — the player
 * understands the tutorial is done and the system is ready to grow.
 *
 * The legacy difficulty / faction / accept-flow types are kept so any
 * old import sites don't break, even though no card is rendered now.
 */

export type MissionDifficulty = 'easy' | 'standard' | 'hard' | 'epic';

export interface MissionEntry {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly reward: string;
  readonly difficulty: MissionDifficulty;
  readonly faction?: string;
}

type TabId = 'main' | 'contracts';

interface TabSpec {
  readonly id: TabId;
  readonly label: string;
  readonly heading: string;
  readonly tagline: string;
  readonly description: string;
}

const TABS: readonly TabSpec[] = [
  {
    id: 'main',
    label: 'Main Mission',
    heading: 'Main Mission',
    tagline: 'Coming Soon',
    description:
      'A long-form storyline is being prepared for Starfall Factions. Until it lands, the sector is yours to roam — mine, trade, dogfight, and accept routine work as it appears here.'
  },
  {
    id: 'contracts',
    label: 'Contracts',
    heading: 'Contracts',
    tagline: 'Coming Soon',
    description:
      'Procedurally generated contracts will surface here — ferry runs, escort jobs, bounty calls and quiet salvage requests. None are live yet. The terminal is ready; the network is not.'
  }
];

export class MissionOverlay {
  readonly element: HTMLElement;

  private readonly rootWrap: HTMLElement;
  private readonly tabBar: HTMLElement;
  private readonly bodyEl: HTMLElement;
  private readonly tabButtons = new Map<TabId, HTMLButtonElement>();
  private activeTab: TabId = 'main';
  private visible = false;

  constructor(private readonly onClose: () => void) {
    this.element = document.createElement('div');
    this.element.className = 'mission-overlay';

    const backdrop = document.createElement('div');
    backdrop.className = 'mission-backdrop';

    this.rootWrap = document.createElement('div');
    this.rootWrap.className = 'mission-root';

    const header = document.createElement('header');
    header.className = 'mission-header';

    const titleBlock = document.createElement('div');
    titleBlock.className = 'mission-title-block';
    const title = document.createElement('h2');
    title.className = 'mission-title';
    title.textContent = 'Mission Terminal';
    const tagline = document.createElement('div');
    tagline.className = 'mission-tagline';
    tagline.textContent = 'Tutorial complete. Free roam unlocked.';
    titleBlock.append(title, tagline);

    const closeBtn = document.createElement('button');
    closeBtn.className = 'mission-close';
    closeBtn.type = 'button';
    closeBtn.textContent = 'Close (E)';
    closeBtn.addEventListener('click', () => onClose());
    header.append(titleBlock, closeBtn);

    this.tabBar = document.createElement('nav');
    this.tabBar.className = 'mission-tabs';
    for (const tab of TABS) {
      const btn = this.buildTabButton(tab);
      this.tabBar.appendChild(btn);
      this.tabButtons.set(tab.id, btn);
    }

    this.bodyEl = document.createElement('section');
    this.bodyEl.className = 'mission-body';

    const footer = document.createElement('footer');
    footer.className = 'mission-footer';
    footer.innerHTML =
      '<span class="mission-footer-note">No live contracts yet — return after future updates.</span>' +
      '<span class="mission-footer-keys"><kbd>E</kbd> or <kbd>ESC</kbd> to close</span>';

    this.rootWrap.append(header, this.tabBar, this.bodyEl, footer);
    this.element.append(backdrop, this.rootWrap);

    this.applyActiveTab();
  }

  show(): void {
    this.element.classList.add('is-visible');
    this.visible = true;
  }

  hide(): void {
    this.element.classList.remove('is-visible');
    this.visible = false;
  }

  isOpen(): boolean {
    return this.visible;
  }

  dispose(): void {
    this.hide();
    this.element.remove();
  }

  private buildTabButton(tab: TabSpec): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.className = 'mission-tab';
    btn.type = 'button';
    btn.dataset['tab'] = tab.id;

    const label = document.createElement('span');
    label.className = 'mission-tab-label';
    label.textContent = tab.label;

    const status = document.createElement('span');
    status.className = 'mission-tab-status';
    status.textContent = 'Coming Soon';

    btn.append(label, status);
    btn.addEventListener('click', () => {
      if (this.activeTab === tab.id) return;
      this.activeTab = tab.id;
      this.applyActiveTab();
    });
    return btn;
  }

  private applyActiveTab(): void {
    for (const [id, btn] of this.tabButtons) {
      btn.classList.toggle('is-active', id === this.activeTab);
    }
    const spec = TABS.find((t) => t.id === this.activeTab) ?? TABS[0]!;
    this.bodyEl.innerHTML = '';

    const card = document.createElement('article');
    card.className = 'mission-empty-card';

    const tag = document.createElement('div');
    tag.className = 'mission-empty-tag';
    tag.textContent = spec.tagline;

    const heading = document.createElement('h3');
    heading.className = 'mission-empty-heading';
    heading.textContent = spec.heading;

    const desc = document.createElement('p');
    desc.className = 'mission-empty-desc';
    desc.textContent = spec.description;

    const ribbon = document.createElement('div');
    ribbon.className = 'mission-empty-ribbon';
    ribbon.textContent =
      'You are free to explore the sector. Trade, mine, fly, fight — the station is open.';

    card.append(tag, heading, desc, ribbon);
    this.bodyEl.appendChild(card);
  }
}
